import React, { useState, useEffect, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { useAuth } from '../../contexts/AuthContext'
import { isPlatformAdminHostname } from '../../config/platformAdminHost'
import {
  CurrencyDollarIcon,
  TableCellsIcon,
  ArrowPathIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'

function todayISO() {
  return new Date().toISOString().slice(0, 10)
}

function weekAgoISO() {
  const d = new Date()
  d.setDate(d.getDate() - 7)
  return d.toISOString().slice(0, 10)
}

export default function BillableUsage() {
  const location = useLocation()
  const { user } = useAuth()
  /** Organisation site: /admin/usage (tenant-scoped). Platform console: /admin/billable-usage (all tenants + rate edit). */
  const tenantMode = location.pathname === '/admin/usage'
  const apiBase = tenantMode ? '/api/billable-usage' : '/api/platform/billable-usage'

  const [tab, setTab] = useState(tenantMode ? 'usage' : 'rates')
  const [services, setServices] = useState([])
  const [summary, setSummary] = useState([])
  const [events, setEvents] = useState([])
  /**
   * Platform: full-page spinner until catalog loads (default tab is Rates).
   * Tenant: catalog loads only when opening Service rates — do not block Usage tab on /services.
   */
  const [loadingServices, setLoadingServices] = useState(() => !tenantMode)
  /** Tenant: loading rate table when first opening Rates tab */
  const [loadingCatalog, setLoadingCatalog] = useState(false)
  /** Usage tab / refresh button */
  const [loadingUsage, setLoadingUsage] = useState(false)
  const [saving, setSaving] = useState({})
  const [from, setFrom] = useState(weekAgoISO)
  const [to, setTo] = useState(todayISO)
  const [filterGroupId, setFilterGroupId] = useState('')
  const [tenants, setTenants] = useState([])
  const [tenantsLoading, setTenantsLoading] = useState(false)
  const [rateDrafts, setRateDrafts] = useState({})

  const loadTenants = useCallback(async () => {
    setTenantsLoading(true)
    try {
      const res = await authFetch(getLocalApiUrl('/api/admin/tenants'))
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
      const list = Array.isArray(data.tenants) ? data.tenants : []
      list.sort((a, b) =>
        String(a.group_id || '').localeCompare(String(b.group_id || ''), undefined, { sensitivity: 'base' })
      )
      setTenants(list)
    } catch (_) {
      setTenants([])
    } finally {
      setTenantsLoading(false)
    }
  }, [])

  useEffect(() => {
    if (tenantMode) return
    loadTenants()
  }, [loadTenants, tenantMode])

  const loadServices = useCallback(async () => {
    const res = await authFetch(getLocalApiUrl(`${apiBase}/services`))
    const json = await res.json()
    if (!json.success) throw new Error(json.error || 'Failed to load services')
    setServices(json.data || [])
    const drafts = {}
    for (const s of json.data || []) {
      drafts[s.service_code] = {
        unit_rate_amount: s.unit_rate_amount,
        currency: s.currency || 'AUD',
      }
    }
    setRateDrafts(drafts)
  }, [apiBase])

  const loadSummary = useCallback(async () => {
    const qs = new URLSearchParams()
    if (from) qs.set('from', from)
    if (to) qs.set('to', to)
    if (!tenantMode && filterGroupId.trim()) qs.set('group_id', filterGroupId.trim())
    const res = await authFetch(getLocalApiUrl(`${apiBase}/usage/summary?${qs}`))
    const json = await res.json()
    if (!json.success) throw new Error(json.error || 'Failed to load summary')
    setSummary(json.data || [])
  }, [from, to, filterGroupId, tenantMode, apiBase])

  const loadEvents = useCallback(async () => {
    const qs = new URLSearchParams()
    if (from) qs.set('from', from)
    if (to) qs.set('to', to)
    if (!tenantMode && filterGroupId.trim()) qs.set('group_id', filterGroupId.trim())
    qs.set('limit', '200')
    const res = await authFetch(getLocalApiUrl(`${apiBase}/usage/events?${qs}`))
    const json = await res.json()
    if (!json.success) throw new Error(json.error || 'Failed to load events')
    setEvents(json.data || [])
  }, [from, to, filterGroupId, tenantMode, apiBase])

  useEffect(() => {
    if (tenantMode) return
    let cancelled = false
    ;(async () => {
      setLoadingServices(true)
      try {
        await loadServices()
      } catch (e) {
        if (!cancelled) toast.error(e.message || 'Load failed')
      } finally {
        if (!cancelled) setLoadingServices(false)
      }
    })()
    return () => { cancelled = true }
  }, [loadServices, tenantMode])

  useEffect(() => {
    if (!tenantMode || tab !== 'rates') return
    if (services.length > 0) return
    let cancelled = false
    ;(async () => {
      setLoadingCatalog(true)
      try {
        await loadServices()
      } catch (e) {
        if (!cancelled) toast.error(e.message || 'Load failed')
      } finally {
        if (!cancelled) setLoadingCatalog(false)
      }
    })()
    return () => { cancelled = true }
  }, [loadServices, tenantMode, tab, services.length])

  useEffect(() => {
    if (tab !== 'usage') return
    let cancelled = false
    ;(async () => {
      setLoadingUsage(true)
      try {
        await loadSummary()
        if (!cancelled) await loadEvents()
      } catch (e) {
        if (!cancelled) toast.error(e.message || 'Load failed')
      } finally {
        if (!cancelled) setLoadingUsage(false)
      }
    })()
    return () => { cancelled = true }
  }, [tab, from, to, filterGroupId, loadSummary, loadEvents])

  const refreshUsage = async () => {
    setLoadingUsage(true)
    try {
      await loadSummary()
      await loadEvents()
      toast.success('Usage refreshed')
    } catch (e) {
      toast.error(e.message || 'Refresh failed')
    } finally {
      setLoadingUsage(false)
    }
  }

  const saveRate = async (serviceCode) => {
    if (tenantMode) return
    const draft = rateDrafts[serviceCode]
    if (!draft) return
    setSaving((s) => ({ ...s, [serviceCode]: true }))
    try {
      const res = await authFetch(
        getLocalApiUrl(`/api/platform/billable-usage/services/${encodeURIComponent(serviceCode)}`),
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            unit_rate_amount: Number(draft.unit_rate_amount),
            currency: draft.currency || 'AUD',
          }),
        }
      )
      const json = await res.json()
      if (!json.success) throw new Error(json.error || 'Save failed')
      toast.success(`Updated ${serviceCode}`)
      await loadServices()
    } catch (e) {
      toast.error(e.message || 'Save failed')
    } finally {
      setSaving((s) => ({ ...s, [serviceCode]: false }))
    }
  }

  if (tenantMode && isPlatformAdminHostname()) {
    return (
      <div className="bg-white shadow rounded-lg p-6 max-w-lg space-y-2">
        <h1 className="text-xl font-semibold text-gray-900">Third-party usage</h1>
        <p className="text-gray-600">
          Organisation usage is available on a tenant URL (e.g. <code className="text-sm bg-gray-100 px-1 rounded">tsg.localhost:3001</code>).
          For all tenants and rate editing, use <strong>Billable usage</strong> from the platform menu.
        </p>
      </div>
    )
  }

  if (tenantMode && !user?.group_id) {
    return (
      <div className="bg-white shadow rounded-lg p-6 max-w-lg">
        <h1 className="text-xl font-semibold text-gray-900">Third-party usage</h1>
        <p className="mt-2 text-gray-600">
          Your account is not linked to an organisation ({' '}
          <code className="text-sm bg-gray-100 px-1 rounded">group_id</code>). Open this app from your organisation&rsquo;s site
          (tenant subdomain) and sign in again.
        </p>
      </div>
    )
  }

  if (!tenantMode && loadingServices) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-lg p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
          <CurrencyDollarIcon className="h-8 w-8 text-primary-600" />
          {tenantMode ? 'Third-party usage' : 'Billable third-party usage'}
        </h1>
        <p className="text-gray-600">
          {tenantMode ? (
            <>
              Estimated usage and costs for <strong>your organisation only</strong>, based on platform unit rates.
              Events are recorded when integrations (DVS, screening, etc.) run.
            </>
          ) : (
            <>
              Configure unit rates (K1–C4) and review usage by tenant (<code className="text-sm bg-gray-100 px-1 rounded">group_id</code>).
              Events are recorded when the backend invokes external integrations or stubs.
            </>
          )}
        </p>
      </div>

      <div className="border-b border-gray-200 bg-white rounded-t-lg px-4 flex gap-4">
        <button
          type="button"
          onClick={() => setTab('rates')}
          className={`py-3 px-2 border-b-2 text-sm font-medium ${
            tab === 'rates'
              ? 'border-primary-600 text-primary-700'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <span className="inline-flex items-center gap-1">
            <TableCellsIcon className="h-4 w-4" /> Service rates
          </span>
        </button>
        <button
          type="button"
          onClick={() => setTab('usage')}
          className={`py-3 px-2 border-b-2 text-sm font-medium ${
            tab === 'usage'
              ? 'border-primary-600 text-primary-700'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <span className="inline-flex items-center gap-1">
            <ChartBarIcon className="h-4 w-4" /> Usage &amp; audit
          </span>
        </button>
      </div>

      {tab === 'rates' && (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Rate per billable unit</h2>
            <p className="text-sm text-gray-500 mt-1">
              {tenantMode
                ? 'Reference rates used to estimate cost for your organisation. Platform administrators can change catalog rates.'
                : 'Amounts are platform defaults for estimated cost in reports; your finance model may differ.'}
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Rate</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Currency</th>
                  {!tenantMode && (
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Action</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loadingCatalog && services.length === 0 ? (
                  <tr>
                    <td
                      colSpan={tenantMode ? 5 : 6}
                      className="px-4 py-8 text-center text-gray-500 text-sm"
                    >
                      Loading service catalog…
                    </td>
                  </tr>
                ) : (
                  services.map((s) => (
                  <tr key={s.service_code}>
                    <td className="px-4 py-3 text-sm font-mono font-medium text-gray-900">{s.service_code}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{s.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{s.category}</td>
                    <td className="px-4 py-3 text-sm text-right">
                      {tenantMode ? (
                        <span className="tabular-nums">{s.unit_rate_amount ?? '—'}</span>
                      ) : (
                        <input
                          type="number"
                          step="0.0001"
                          className="w-28 rounded border border-gray-300 px-2 py-1 text-right text-sm"
                          value={rateDrafts[s.service_code]?.unit_rate_amount ?? ''}
                          onChange={(e) =>
                            setRateDrafts((d) => ({
                              ...d,
                              [s.service_code]: {
                                ...d[s.service_code],
                                unit_rate_amount: e.target.value,
                                currency: d[s.service_code]?.currency || s.currency,
                              },
                            }))
                          }
                        />
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {tenantMode ? (
                        <span className="uppercase">{s.currency || 'AUD'}</span>
                      ) : (
                        <input
                          type="text"
                          maxLength={8}
                          className="w-20 rounded border border-gray-300 px-2 py-1 text-sm uppercase"
                          value={rateDrafts[s.service_code]?.currency ?? s.currency ?? 'AUD'}
                          onChange={(e) =>
                            setRateDrafts((d) => ({
                              ...d,
                              [s.service_code]: {
                                ...d[s.service_code],
                                unit_rate_amount: d[s.service_code]?.unit_rate_amount ?? s.unit_rate_amount,
                                currency: e.target.value,
                              },
                            }))
                          }
                        />
                      )}
                    </td>
                    {!tenantMode && (
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          className="btn-secondary text-sm"
                          disabled={!!saving[s.service_code]}
                          onClick={() => saveRate(s.service_code)}
                        >
                          {saving[s.service_code] ? 'Saving…' : 'Save'}
                        </button>
                      </td>
                    )}
                  </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'usage' && (
        <div className="space-y-4">
          {loadingUsage && (
            <p className="text-sm text-gray-500" aria-live="polite">
              Loading usage for this date range…
            </p>
          )}
          <div className="bg-white shadow rounded-lg p-4 flex flex-wrap gap-4 items-end">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">From</label>
              <input
                type="date"
                className="rounded border border-gray-300 px-2 py-1 text-sm"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">To</label>
              <input
                type="date"
                className="rounded border border-gray-300 px-2 py-1 text-sm"
                value={to}
                onChange={(e) => setTo(e.target.value)}
              />
            </div>
            {!tenantMode && (
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Tenant</label>
                <select
                  className="rounded border border-gray-300 px-2 py-1.5 text-sm min-w-[14rem] max-w-xs"
                  value={filterGroupId}
                  onChange={(e) => setFilterGroupId(e.target.value)}
                  disabled={tenantsLoading}
                  aria-label="Filter by tenant"
                >
                  <option value="">All tenants</option>
                  {tenants.map((t) => {
                    const gid = t.group_id || t.groupId
                    if (!gid) return null
                    const name = t.name || t.organisation_name || t.org_name || ''
                    return (
                      <option key={gid} value={gid}>
                        {name ? `${name} (${gid})` : gid}
                      </option>
                    )
                  })}
                </select>
              </div>
            )}
            <button type="button" className="btn-primary inline-flex items-center gap-1" onClick={refreshUsage} disabled={loadingUsage}>
              <ArrowPathIcon className={`h-4 w-4 ${loadingUsage ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>

          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {tenantMode ? 'Summary by service' : 'Summary by tenant & service'}
              </h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {!tenantMode && (
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">group_id</th>
                    )}
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Service</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Events</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Units</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Est. amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {summary.length === 0 ? (
                    <tr>
                      <td colSpan={tenantMode ? 4 : 5} className="px-4 py-8 text-center text-gray-500 text-sm">
                        No usage in this range. Trigger a mobile DVS stub, AML check, or patron screening to see rows.
                      </td>
                    </tr>
                  ) : (
                    summary.map((row, i) => (
                      <tr key={`${row.group_id}-${row.service_code}-${i}`}>
                        {!tenantMode && (
                          <td className="px-4 py-3 text-sm font-mono text-gray-800">{row.group_id}</td>
                        )}
                        <td className="px-4 py-3 text-sm">
                          <span className="font-mono font-medium">{row.service_code}</span>
                          <span className="text-gray-500 ml-2">{row.service_name}</span>
                        </td>
                        <td className="px-4 py-3 text-sm text-right">{row.event_count}</td>
                        <td className="px-4 py-3 text-sm text-right">{row.total_units}</td>
                        <td className="px-4 py-3 text-sm text-right">
                          {row.currency}{' '}
                          {Number(row.estimated_amount || 0).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 4,
                          })}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Recent events</h2>
            </div>
            <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Time</th>
                    {!tenantMode && (
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">group_id</th>
                    )}
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Svc</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Integration</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Vendor</th>
                    <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">Stub</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {events.length === 0 ? (
                    <tr>
                      <td colSpan={tenantMode ? 5 : 6} className="px-4 py-6 text-center text-gray-500">
                        No events.
                      </td>
                    </tr>
                  ) : (
                    events.map((ev) => (
                      <tr key={ev.id}>
                        <td className="px-3 py-2 whitespace-nowrap text-gray-600">
                          {ev.occurred_at ? new Date(ev.occurred_at).toLocaleString() : '—'}
                        </td>
                        {!tenantMode && (
                          <td className="px-3 py-2 font-mono text-xs">{ev.group_id}</td>
                        )}
                        <td className="px-3 py-2 font-mono">{ev.service_code}</td>
                        <td className="px-3 py-2 text-gray-600">{ev.integration_point || '—'}</td>
                        <td className="px-3 py-2 text-gray-600">{ev.vendor_code || '—'}</td>
                        <td className="px-3 py-2 text-center">{ev.is_stub ? 'yes' : 'no'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
